import axios from 'axios';
import Cookies from 'js-cookie';
const controllers = {};

export function cancelPrevFetch(funcName) {
  if (controllers[funcName]) {
    controllers[funcName].abort();
    controllers[funcName] = undefined;
  }
}

// API instances
const pfApiInstance = axios.create({
  baseURL: '/api/portfolio',
  headers: {
    'X-CSRFToken': Cookies.get('csrftoken'),
    'Content-Type': 'application/json',
    accept: 'application/json',
  },
});

//makes sure to send latest token
pfApiInstance.interceptors.request.use(
  (config) => {
    config.headers['X-CSRFToken'] = Cookies.get('csrftoken');
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const subscriptionAPIInstance = axios.create({
  baseURL: '/api/subscription',
  headers: {
    'X-CSRFToken': Cookies.get('csrftoken'),
    'Content-type': 'application/json',
    accept: 'application/json',
  },
});

const userApiInstance = axios.create({
  baseURL: '/api/users',
  headers: {
    'X-CSRFToken': Cookies.get('csrftoken'),
    'Content-type': 'application/json',
    accept: 'application/json',
  },
});

// mututal funds
const smallcaseApiInstance = axios.create({
  baseURL: '/in/portfolio/smallcase',
  headers: {
    'X-CSRFToken': Cookies.get('csrftoken'),
    'Content-type': 'application/json',
    accept: 'application/json',
  },
});

export function requestMFOtp(payload) {
  return smallcaseApiInstance.post('/send-phone-pan', payload, {
    headers: {
      'X-CSRFToken': Cookies.get('csrftoken'),
      'Content-type': 'application/json',
    },
  });
}

export function verifyMFOtp(payload) {
  return smallcaseApiInstance.post('/verify-otp', payload, {
    headers: {
      'X-CSRFToken': Cookies.get('csrftoken'),
      'Content-type': 'application/json',
    },
  });
}

// APIs
export function getPortfolioMeta(portfolio_id) {
  return pfApiInstance.get(`${portfolio_id}/portfolio_meta/`);
}

export function fetchUserHoldingsKite(portfolio_id) {
  return pfApiInstance.post(
    `${portfolio_id}/kite/sync/`,
    {},
    {
      headers: {
        'X-CSRFToken': Cookies.get('csrftoken'),
        'Content-type': 'application/json',
      },
    },
  );
}

export function fetchUserHoldingsSmallcase(portfolio_id, sc_auth_token) {
  return pfApiInstance.post(`${portfolio_id}/broker/sync/`, {
    token: sc_auth_token,
  });
}

export function generateSmallCaseTransaction(portfolio_id) {
  return pfApiInstance.get(`${portfolio_id}/broker/connect`);
}

export function fetchImportSummarySmallcase(portfolio_id) {
  return pfApiInstance.get(`${portfolio_id}/broker/summary/`);
}

export function updateImportSummarySmallcase(portfolio_id) {
  return pfApiInstance.post(
    `${portfolio_id}/broker/summary/`,
    {},
    {
      headers: {
        'X-CSRFToken': Cookies.get('csrftoken'),
        'Content-type': 'application/json',
      },
    },
  );
}

export function searchAll(q) {
  //header search
  return axios.get('/api/v1/ind/company_search/', {
    params: { q },
  });
}

export function searchCompany(q) {
  return axios.get('/api/india/search/', {
    params: { q },
    headers: {
      'X-CSRFToken': Cookies.get('csrftoken'),
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  });
}

export function searchUniversal(q) {
  return axios.get('/api/india/universal_search/', {
    params: { q },
    headers: {
      'X-CSRFToken': Cookies.get('csrftoken'),
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  });
}

export function createPortfolio(name) {
  return pfApiInstance.post(`create/`, { title: name });
}

export function renamePortfolio(portfolio_id, name) {
  return pfApiInstance.post(`${portfolio_id}/rename/`, { title: name });
}

export function fetchSummary(portfolio_id) {
  return pfApiInstance.get(`${portfolio_id}/summary/`);
}

export function fetchCompanySummary(portfolio_id) {
  const controller = new AbortController();
  controllers.fetchCompanySummary = controller;
  return pfApiInstance.get(`${portfolio_id}/company_summary/`, {
    signal: controller.signal,
  });
}

export function fetchFamilySummary(fromTracking = false) {
  if (fromTracking) {
    const controller = new AbortController();
    controllers.fetchCompanySummary = controller;
    return pfApiInstance.get(`family_summary/`, {
      signal: controller.signal,
    });
  }
  return pfApiInstance.get(`family_summary/`);
}

export function fetchCompanyPerformanceIndex(
  portfolio_id,
  days,
  index_type,
  index_id,
  mode,
  xirr,
) {
  return pfApiInstance.get(
    `${portfolio_id}/performance/index/?days=${days}&index_type=${index_type}&index_id=${index_id}&mode=${mode}&xirr=${xirr}`,
  );
}

export function fetchSpa(portfolio_id, instrument) {
  const controller = new AbortController();
  controllers[`fetchSpa_${instrument}`] = controller;
  return pfApiInstance.get(
    portfolio_id === 'family'
      ? `family/performance/spa/?instrument=${instrument}`
      : `${portfolio_id}/performance/spa/?instrument=${instrument}`,
    {
      signal: controller.signal,
    },
  );
}

export function fetchSpaPerIndex(index_type, index_id) {
  return pfApiInstance.get(
    `performance/spa/?index_type=${index_type}&index_id=${index_id}`,
  );
}

export function fetchBSDTxs(portfolio_id, data, type) {
  return pfApiInstance.post(
    `${portfolio_id}/transactions/get_bsd/?type=${type}`,
    data,
  );
}

export function saveBSDTxs(portfolio_id, data) {
  return pfApiInstance.post(`${portfolio_id}/transactions/save/`, data);
}

export function resetPortfolio(portfolio_id, data = {}) {
  //reset client side data
  window.localStorage.removeItem('pf_dcf_discount');
  window.localStorage.removeItem('pf_dcf_table_data');
  return pfApiInstance.post(`${portfolio_id}/reset/`, data);
}

export function deletePortfolio(portfolio_id) {
  //reset client side data
  window.localStorage.removeItem('pf_dcf_discount');
  window.localStorage.removeItem('pf_dcf_table_data');
  return pfApiInstance.post(`${portfolio_id}/delete/`);
}

export function removeCompany(portfolio_id, fincode) {
  return pfApiInstance.post(`${portfolio_id}/company/${fincode}/remove/`);
}

export function getPortfolioVsMutualFund(portfolio_id) {
  return pfApiInstance.get(`${portfolio_id}/pf_vs_mf/`);
}

export function getShareholdingAnalysis(portfolio_id, timeline) {
  const controller = new AbortController();
  controllers.getShareholdingAnalysis = controller;
  return pfApiInstance.get(
    `${portfolio_id}/sh_analysis/?timeline=${timeline}`,
    {
      signal: controller.signal,
    },
  );
}

export function getFinancialAnalysis(portfolio_id, timeline) {
  const controller = new AbortController();
  controllers.getFinancialAnalysis = controller;
  return pfApiInstance.get(
    `${portfolio_id}/fr_analysis/?timeline=${timeline}`,
    {
      signal: controller.signal,
    },
  );
}

export function fetchBreakup(portfolio_id) {
  return pfApiInstance.get(`${portfolio_id}/exposure/breakup/`);
}

export function fetchRiskAnalysis(portfolio_id) {
  return portfolio_id === 'family'
    ? pfApiInstance.get('family/risk_weightage/')
    : pfApiInstance.get(`${portfolio_id}/exposure/risk_analysis/`);
}

export function fetchRiskWeightage(portfolio_id) {
  const controller = new AbortController();
  controllers.fetchRiskWeightage = controller;
  return portfolio_id === 'family'
    ? pfApiInstance.get('family/risk_weightage/', {
        signal: controller.signal,
      })
    : pfApiInstance.post(
        `analysis/risk_breakup/`,
        {
          portfolio_id,
        },
        {
          signal: controller.signal,
        },
      );
}

export function fetchProductExposure(portfolio_id) {
  const controller = new AbortController();
  controllers.fetchProductExposure = controller;
  return portfolio_id === 'family'
    ? pfApiInstance.get('family/prod_bkup/', {
        signal: controller.signal,
      })
    : pfApiInstance.post(
        `analysis/prod_breakup/`,
        {
          portfolio_id,
        },
        {
          signal: controller.signal,
        },
      );
}

export function fetchGeographyExposure(portfolio_id) {
  const controller = new AbortController();
  controllers.fetchGeographyExposure = controller;
  return portfolio_id === 'family'
    ? pfApiInstance.get('family/geog_bkup/', {
        signal: controller.signal,
      })
    : pfApiInstance.post(
        `analysis/geog_breakup/`,
        {
          portfolio_id,
        },
        {
          signal: controller.signal,
        },
      );
}

export function fetchTopHoldings(portfolio_id) {
  const controller = new AbortController();
  controllers.fetchTopHoldings = controller;
  return portfolio_id === 'family'
    ? pfApiInstance.get('family/top_holdings/', {
        signal: controller.signal,
      })
    : pfApiInstance.post(
        `analysis/top_holdings/`,
        {
          portfolio_id,
        },
        {
          signal: controller.signal,
        },
      );
}

export function fetchBenchmarkPE(portfolio_id) {
  const controller = new AbortController();
  controllers.fetchBenchmarkPE = controller;
  return portfolio_id === 'family'
    ? pfApiInstance.get('family/pe/', {
        signal: controller.signal,
      })
    : pfApiInstance.post(
        `analysis/pe_ratio/`,
        {
          portfolio_id,
        },
        {
          signal: controller.signal,
        },
      );
}

export function fetchAssetWeightage(portfolio_id) {
  const controller = new AbortController();
  controllers.fetchAssetWeightage = controller;
  return portfolio_id === 'family'
    ? pfApiInstance.get('family/asset_weightage/', {
        signal: controller.signal,
      })
    : pfApiInstance.post(
        `analysis/asset_breakup/`,
        {
          portfolio_id,
        },
        {
          signal: controller.signal,
        },
      );
}

export function fetchTopGainersLosers(portfolio_id) {
  const controller = new AbortController();
  controllers.fetchTopGainersLosers = controller;
  return pfApiInstance.get(`${portfolio_id}/gainers_losers/`, {
    signal: controller.signal,
  });
}

export function fetchOverlap() {
  return pfApiInstance.get(`family/overlap/`);
}

export function fetchCorrelation(portfolio_id) {
  const controller = new AbortController();
  controllers.fetchCorrelation = controller;
  return pfApiInstance.get(`${portfolio_id}/exposure/correlation/`, {
    signal: controller.signal,
  });
}

export function getStockPriceOnDate(fincode, on_date) {
  return pfApiInstance.get(
    `stock_price/?fincode=${fincode}&on_date=${on_date}`,
  );
}

export function getCompanyTransactions(portfolio_id, fincode) {
  return pfApiInstance.get(`${portfolio_id}/company/${fincode}/transactions/`);
}

export function removeTransaction(portfolio_id, tx_id) {
  return pfApiInstance.post(`${portfolio_id}/transaction/${tx_id}/remove/`);
}

export function fetchRiskReturnHighlight(portfolio_id) {
  return pfApiInstance.get(`${portfolio_id}/highlight/risk_return/`);
}

export function parseTransactions(portfolio_id, data, fuzzy = false) {
  return pfApiInstance.post(
    `${portfolio_id}/transactions/parse?fuzzy=${fuzzy}`,
    data,
  );
}

export function saveBetaUser(data) {
  return pfApiInstance.post('apply-for-beta/', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

export function getPeers(portfolio_id) {
  return pfApiInstance.get(`${portfolio_id}/peers/`);
}

export function contactUs(payload) {
  return subscriptionAPIInstance.post(
    '/contact-us/',
    { ...payload, source: 'portfolio' },
    {
      headers: {
        'X-CSRFToken': Cookies.get('csrftoken'),
        'Content-type': 'application/json',
      },
    },
  );
}

export function getRiskBeakup(company_ids, company_weights) {
  return pfApiInstance.post('what-if/risk_breakup/', {
    company_ids,
    company_weights,
  });
}

export function getProductBreakup(company_ids, company_weights) {
  return pfApiInstance.post('what-if/prod_breakup/', {
    company_ids,
    company_weights,
  });
}

export function getGeogBreakup(company_ids, company_weights) {
  return pfApiInstance.post('what-if/geog_breakup/', {
    company_ids,
    company_weights,
  });
}

export function getMarketCapBreakup(company_ids, company_weights) {
  return pfApiInstance.post('what-if/market_cap_breakup/', {
    company_ids,
    company_weights,
  });
}

export function getPERatio(company_ids, company_weights) {
  return pfApiInstance.post('what-if/pe_ratio/', {
    company_ids,
    company_weights,
  });
}

export function getROE(company_ids, company_weights) {
  return pfApiInstance.post('what-if/roe/', {
    company_ids,
    company_weights,
  });
}

export function getNetProfitMargin(company_ids, company_weights) {
  return pfApiInstance.post('what-if/net_profit_margin/', {
    company_ids,
    company_weights,
  });
}

export function getOpProfitMargin(company_ids, company_weights) {
  return pfApiInstance.post('what-if/op_profit_margin/', {
    company_ids,
    company_weights,
  });
}

export function getDebtToEqity(company_ids, company_weights) {
  return pfApiInstance.post('what-if/debt_to_equity/', {
    company_ids,
    company_weights,
  });
}

export function getWiCorrelation(company_ids) {
  return pfApiInstance.post('what-if/correlation/', {
    company_ids,
  });
}

export function updateExistingTxs(portfolio_id, payload) {
  return pfApiInstance.post(`${portfolio_id}/transactions/edit/`, payload);
}

export function getReverseDCF(portfolio_id) {
  return pfApiInstance.get(`${portfolio_id}/dcf/`);
}

export function getRedFlags(portfolio_id) {
  const controller = new AbortController();
  controllers.getRedFlags = controller;
  return pfApiInstance.get(`${portfolio_id}/red_flags/`, {
    signal: controller.signal,
  });
}

export function getCashInOut(portfolio_id, timeline, start, end) {
  let queryParams = `timeline=${timeline}`;
  if (start && end) {
    queryParams = `start=${start}&end=${end}`;
  }
  const isFamily = portfolio_id === 'family';

  const controller = new AbortController();
  controllers.getCashInOut = controller;
  return pfApiInstance.get(
    isFamily
      ? `family/cash_in_out?${queryParams}`
      : `${portfolio_id}/cash_in_out/?${queryParams}`,
    {
      signal: controller.signal,
    },
  );
}

export function fetchGain(portfolio_id, startDate, endDate) {
  return pfApiInstance.get(
    `${portfolio_id}/gain/?start=${startDate}&end=${endDate}`,
  );
}

export function sendOnboardingMail() {
  return userApiInstance.post(
    '/account/send/onboard_mail/',
    {},
    {
      headers: {
        'X-CSRFToken': Cookies.get('csrftoken'),
        'Content-type': 'application/json',
      },
    },
  );
}
